/**
 * Holding the popup while the FlowIQ chat is open.
 *
 * The chat widget (react-chatwidget-flowpat, ChatWidget.tsx) marks <html> with
 * `data-flowiq-chat-open` while its panel is open. A popup whose automatic
 * trigger (timer, scroll, exit intent) fires during a conversation would cover
 * the chat mid-sentence (13nutrition.co.za, 20 Oct 2026), so the trigger waits
 * here until the chat closes and then a short pause more.
 *
 * Only the automatic trigger waits. The chat's own "Get 10% off" card opens the
 * popup on purpose through ReactPopupWidget.open(), which never comes here.
 * A chat build without the marker never sets it, so the popup behaves as before.
 */
export const CHAT_OPEN_ATTR = "data-flowiq-chat-open";

export function isFlowiqChatOpen(doc: Document = document): boolean {
  return doc.documentElement.hasAttribute(CHAT_OPEN_ATTR);
}

/**
 * Runs `fire` now when no chat is open; otherwise once the chat has been
 * closed for `pauseMs` (re-opening during the pause starts the wait again).
 * Returns a cleanup that cancels a pending fire.
 */
export function whenChatClosed(
  fire: () => void,
  { pauseMs = 2000, doc = document }: { pauseMs?: number; doc?: Document } = {}
): () => void {
  if (!isFlowiqChatOpen(doc)) {
    fire();
    return () => {};
  }

  let pause: ReturnType<typeof setTimeout> | null = null;
  const observer = new MutationObserver(() => {
    if (isFlowiqChatOpen(doc)) {
      if (pause) clearTimeout(pause);
      pause = null;
      return;
    }
    if (pause) return;
    pause = setTimeout(() => {
      pause = null;
      if (isFlowiqChatOpen(doc)) return;
      observer.disconnect();
      fire();
    }, pauseMs);
  });
  observer.observe(doc.documentElement, {
    attributes: true,
    attributeFilter: [CHAT_OPEN_ATTR],
  });

  return () => {
    observer.disconnect();
    if (pause) clearTimeout(pause);
  };
}
